import { TICKET_CAP_ERROR_CODE } from './RecordingUploadController';
import type { LocalRecordingUpload } from './types';

/**
 * The single arc a person sees between stopping a walkthrough and acting on
 * its tickets (TOADO-304).
 *
 * The recorder tracks three pipelines that finish at different times: the
 * master video upload, per-segment clip cutting, and transcription. Showing
 * them as three independent statuses answers "what is the system doing" but
 * never answers "how long until I can act on this", which is the only question
 * anyone actually has at that moment.
 *
 * The review page derives the same arc from the server's session rather than
 * from this local record, in `reviewProgress.ts`: it is reachable from another
 * machine, after a reload, or by a colleague, none of which have a local
 * upload to read. The two share the stage names so they cannot describe one
 * walkthrough in different words.
 *
 * Transcription is deliberately NOT part of the arc. It is the slowest stage,
 * retries with exponential backoff up to ten attempts, and is off entirely
 * unless both TRANSCRIPTION_ENABLED and OPENAI_TRANSCRIPTION_POLICY_PUBLISHED
 * are set — so gating "ready" on it would mean staring at a bar while a
 * perfectly reviewable ticket set sits behind it, and would make the arc
 * depend on a feature that is disabled by default. Transcripts land underneath
 * as they arrive.
 */
export type RecordingProgressStage = 'uploading' | 'processing' | 'ready' | 'failed';

export interface RecordingProgress {
  stage: RecordingProgressStage;
  /** 0-100 for `uploading`; undefined when no meaningful fraction exists. */
  percent?: number;
  /** Short, plain statement of what is happening now. */
  title: string;
  /** One line of context, or what to do about a failure. */
  detail: string;
  /** True once the review page can be acted on. */
  actionable: boolean;
  /** Present only on `failed`, for support and logs. */
  errorCode?: string;
}

const NOT_STARTED: RecordingProgress = {
  stage: 'uploading',
  percent: 0,
  title: 'Preparing secure upload',
  detail: 'Your walkthrough is saved locally until the server acknowledges it.',
  actionable: false,
};

function uploadPercent(upload: LocalRecordingUpload): number | undefined {
  if (!Number.isFinite(upload.totalBytes) || upload.totalBytes <= 0) return undefined;
  const ratio = upload.offset / upload.totalBytes;
  if (!Number.isFinite(ratio)) return undefined;
  return Math.min(100, Math.max(0, Math.round(ratio * 100)));
}

/**
 * Collapse an upload record into the one stage worth showing.
 *
 * A missing upload is treated as not-started rather than as an error: the
 * record is written asynchronously after the final chunk settles, so there is
 * a real window where a just-stopped recording has no upload row yet.
 */
export function deriveRecordingProgress(
  upload: LocalRecordingUpload | undefined,
): RecordingProgress {
  if (!upload) return NOT_STARTED;

  switch (upload.status) {
    case 'failed':
      /**
       * A refused walkthrough is not a failed one, and must not be offered a
       * retry (TOADO-372). Nothing was uploaded, nothing was transcribed, and
       * pressing retry until the plan changes only reproduces the refusal.
       * The server's own sentence is used verbatim: it knows the cap, what is
       * used and how many moments this walkthrough marked.
       */
      if (upload.lastErrorCode === TICKET_CAP_ERROR_CODE) {
        return {
          stage: 'failed',
          title: 'Your plan has no tickets left this month',
          // No plan names here. What resolves this is not always an upgrade -
          // a workspace can be capped by an admin override on any plan - and
          // the server is the only side that knows which. When it sent no
          // sentence, say the fact and point at the place that does know.
          detail: upload.lastErrorMessage
            ?? 'This walkthrough was not uploaded. Check your workspace ticket limit in billing.',
          actionable: false,
          errorCode: upload.lastErrorCode,
        };
      }
      return {
        stage: 'failed',
        title: 'This walkthrough needs a retry',
        // Never imply the recording is gone. Local chunks are retained until
        // the server acknowledges them, and saying so is the difference
        // between a retry and someone re-recording twenty minutes of work.
        detail: 'Your recording is safe locally. Retry the upload to continue.',
        actionable: false,
        ...(upload.lastErrorCode ? { errorCode: upload.lastErrorCode } : {}),
      };

    case 'ready':
      return {
        stage: 'ready',
        title: 'Ready to create tickets',
        detail: 'Every marked moment has its clip and screenshot.',
        actionable: true,
      };

    // `cleanup` is post-success local housekeeping. Surfacing it as its own
    // stage would move the bar backwards from the user's point of view, so it
    // reads as the tail of processing.
    case 'processing':
    case 'cleanup':
      return {
        stage: 'processing',
        title: 'Cutting your ticket clips',
        detail: 'The server is slicing the walkthrough at each ticket boundary.',
        actionable: false,
      };

    case 'uploading':
    case 'pending':
    default: {
      const percent = uploadPercent(upload);
      return {
        stage: 'uploading',
        ...(percent === undefined ? {} : { percent }),
        title: percent === undefined ? 'Uploading your walkthrough' : `Uploading your walkthrough · ${percent}%`,
        detail: 'You can keep working. This continues in the background.',
        actionable: false,
      };
    }
  }
}

/**
 * True when the arc has reached a point worth navigating the user to.
 *
 * Used to hand off automatically on stop (TOADO-303) rather than leaving them
 * to find the review page.
 *
 * Failure deliberately does NOT hand off. The upload retry lives on the
 * recorder page, next to the local chunks it would resend; the review page has
 * clip and transcript retries but no way to resume a master upload, so
 * navigating on failure walks the person away from the only control that could
 * fix it. TOADO-303 says this outright: "a stop that fails does not navigate;
 * it says what went wrong and leaves the recovery options in reach."
 */
export function shouldHandOffToReview(progress: RecordingProgress): boolean {
  return progress.stage === 'ready';
}

/**
 * How long a walkthrough may sit mid-pipeline before we stop calling it
 * progress and start calling it stuck (TOADO-306).
 *
 * This governs the stages before the server takes over: a session left
 * `recording` with no bytes moving really has been abandoned, and silence is
 * the only signal available. Once processing starts, `inFlightSessions.ts`
 * switches to the worker's processing lease instead, because the worker does
 * not touch the row while it re-encodes and a long walkthrough legitimately
 * runs past any threshold short enough to be useful here.
 */
export const RECORDING_STALL_AFTER_MS = 15 * 60 * 1_000;
